import { orderBy } from 'lodash-es'
import { useMemo, useState } from 'react'

import { useQuery } from '@fv/client-core'
import type {
  AccountCarrierMode,
  CarrierConnectionDetailsDto,
  CarrierConnectionDto,
  CarrierProvisioningConfiguration,
} from '@fv/models'

import { useFuseSearch } from '@/hooks/useFuseSearch'
import { shipperFetch } from '@/utils/shipperFetch'

import { MIN_CARRIER_SEARCH_LENGTH } from './utils'

export const carrierNetworkKeys = {
  all: ['carrier-network'],
  details: (carrierId: string) => [...carrierNetworkKeys.all, carrierId],
  search: (query: string) => [...carrierNetworkKeys.all, 'search', query],
  carrierConfiguration: (carrierId: string) => [
    'carrier-configuration',
    carrierId,
  ],
}

export const useCarrierNetwork = (enabled: boolean = true) => {
  return useQuery(
    carrierNetworkKeys.all,
    () => shipperFetch<CarrierConnectionDto[]>('/carrier-network'),
    { enabled },
  )
}

export const useCarrierNetworkMap = (enabled: boolean = true) => {
  const { data: networkCarriers } = useCarrierNetwork(enabled)
  return useMemo(() => {
    return new Map(networkCarriers?.map(c => [c.carrierId, c]) ?? [])
  }, [networkCarriers])
}

export const useCarrierConnectionDetails = (carrierId: string | null) => {
  return useQuery(
    carrierNetworkKeys.details(carrierId ?? ''),
    () =>
      shipperFetch<CarrierConnectionDetailsDto>(
        `/carrier-network/${carrierId}`,
      ),
    { enabled: !!carrierId },
  )
}

export const useCarrierSearch = (query: string) => {
  return useQuery(
    carrierNetworkKeys.search(query),
    () =>
      shipperFetch<CarrierProvisioningConfiguration[]>(
        `/carrier-network/search?q=${encodeURIComponent(query)}`,
      ),
    { enabled: query.length >= MIN_CARRIER_SEARCH_LENGTH },
  )
}

export const useCarrierConfiguration = (carrierId: string) => {
  return useQuery(carrierNetworkKeys.carrierConfiguration(carrierId), () =>
    shipperFetch<CarrierProvisioningConfiguration>(
      `/carriers/provisioning/${carrierId}`,
    ),
  )
}

export const useProvisionedAccountCarriers = (enabled: boolean = true) => {
  const query = useCarrierNetwork(enabled)
  const data = useMemo(
    () => query.data?.filter(c => c.isApiIntegrated) ?? [],
    [query.data],
  )
  return { ...query, data }
}

interface CarriersFilter {
  mode?: AccountCarrierMode
  onlyApiIntegrated?: boolean
}

const availableCarrierKeys = {
  carriers: (filter?: CarriersFilter) => [
    'provisioning',
    'carriers',
    !!filter?.onlyApiIntegrated,
    filter?.mode ?? 'all',
  ],
}

export const useAvailableCarriers = (filter?: CarriersFilter) => {
  return useQuery(
    availableCarrierKeys.carriers(filter),
    () =>
      shipperFetch<CarrierProvisioningConfiguration[]>(
        `/carriers/provisioning`,
        {
          query: filter,
        },
      ),
    {
      select: data => orderBy(data, c => c.name.trim()),
    },
  )
}

export const useSearchAvailableCarriers = (filter?: CarriersFilter) => {
  const [search, setSearch] = useState('')
  const availableQuery = useAvailableCarriers(filter)
  const normalizedCarriers = useMemo(
    () =>
      availableQuery.data?.map(d => ({
        ...d,
        normalizedName: d.name.replace(/\./g, ''),
      })) ?? [],
    [availableQuery],
  )
  const searchItems = useFuseSearch(normalizedCarriers, {
    keys: ['normalizedName', 'code'],
    threshold: 0.2,
  })

  const searchResults = (
    search
      ? searchItems.search(search).map(i => i.item)
      : (availableQuery.data ?? [])
  ).filter(
    i => i.code !== 'fxfd', // TODO fxfe - we're no longer showing fxfd
  )
  return {
    search,
    setSearch,
    searchResults,
    carrierResults: availableQuery.data ?? [],
    isLoading: availableQuery.isLoading,
  }
}
